import { Radio, RadioChangeEvent } from 'antd';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { AtiraColorPicker } from '../../../../components/AtiraColorPicker';
import { AtiraSelect } from '../../../../components/AtiraSelect';
import { Flex } from '../../../../components/Flex';
import { ImageUpload } from '../../../../components/ImageUpload';
import { PaidFeatureNotice } from '../../../../components/PaidFeatureNotice';
import { Section } from '../../../../components/Section';
import { Text } from '../../../../components/Text';
import { atiraLinkTestIDs } from '../../../../constants/test/atiralink.testIds';
import { CreateAtiraLinkDto } from '../../../../model/atira-link/dto/CreateAtiraLinkDto';
import { AtiraLinkBackgroundImagePosition } from '../../../../model/atira-link/types/AtiraLinkBackgroundImagePosition';
import { AtiraLinkBackgroundImageSize } from '../../../../model/atira-link/types/AtiraLinkBackgroundImageSize';
import { AtiraLinkStylesBackgroundMode } from '../../../../model/atira-link/types/AtiraLinkStylesBackgroundMode.enum';
import { AtiraLinkPlanType } from '../../../../model/plan/types/AtiraLinkPlanType.enum';
import { useAppSelector } from '../../../../redux/store';
import { userSliceSelectors } from '../../../../redux/user/user.selector';
import { Breakpoints } from '../../../../theme/Breakpoints';
import { FontSize } from '../../../../theme/FontSize';
import { Rounded } from '../../../../theme/Rounded';
import { Spacing } from '../../../../theme/Spacing';
import { getUserCurrentPlan } from '../../../../utils/pricingUtils';
import {
  backgroundImagePositionOptions,
  backgroundImageSizeOptions,
} from '../../utils/styleOptions';

const StyledSection = styled(Section)`
  .content-wrapper {
    gap: ${Spacing.l};
  }
`;

const Description = styled(Text)`
  color: ${({ theme }) => theme.fgMuted};
  font-size: ${FontSize.s};
`;

const InputWrapper = styled(Flex)`
  & > div {
    width: 100%;
  }

  @media (min-width: ${Breakpoints.TABLET}) {
    width: 50%;
  }
`;

const ImageUploadWrapper = styled(Flex)`
  .ant-upload-wrapper {
    border: ${({ theme }) => `1px solid ${theme.main}`} !important;
    border-radius: ${Rounded.circle} !important;
  }

  .ant-upload-select {
    border-radius: ${Rounded.circle} !important;
    border: 0 !important;
    overflow: hidden !important;
  }

  .ant-upload-list-picture-card {
    display: block !important;
    position: relative !important;
  }

  .ant-upload-list-item {
    border: 0 !important;
    padding: 0 !important;
    border-radius: ${Rounded.circle} !important;
    overflow: hidden !important;
  }

  .ant-upload-list-item-container {
    position: absolute !important;
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
  }

  .ant-upload-list-item-actions {
    z-index: 5000 !important;
  }

  .ant-upload-list-item::before {
    width: 100% !important;
    height: 100% !important;
  }

  button {
    width: 100%;
    height: 100%;

    &:hover {
      background-color: ${({ theme }) => theme.lightMain} !important;
    }
  }
`;

const ResponsiveFlex = styled(Flex)`
  flex-direction: column;
  gap: ${Spacing.m};

  @media (min-width: ${Breakpoints.TABLET}) {
    flex-direction: row;
  }
`;

export const AtiraLinkStyleBackgroundImage: React.FC = () => {
  const { control, setValue, watch } = useFormContext<CreateAtiraLinkDto>();

  const { t } = useTranslation();

  const [backgroundImage, mode] = watch([
    'styles.backgroundImage',
    'styles.background.backgroundMode',
  ]);

  const user = useAppSelector(userSliceSelectors.selectLoggedInUser)!;

  const canChangeBackground =
    getUserCurrentPlan(user) === AtiraLinkPlanType.PLAN_2;

  const onChangeBackgroundType = (e: RadioChangeEvent) => {
    setValue('styles.background.backgroundMode', e.target.value);
  };

  return (
    <StyledSection title={t('atiraLink.create.design.background.title')}>
      <Description>
        {t('atiraLink.create.design.background.description')}
      </Description>

      <Radio.Group onChange={onChangeBackgroundType} value={mode}>
        <Radio
          value={AtiraLinkStylesBackgroundMode.COLOR}
          className={atiraLinkTestIDs.design.background.options.asColor}
        >
          {t('atiraLink.create.design.background.asColor')}
        </Radio>
        <PaidFeatureNotice
          asPopup
          plan={AtiraLinkPlanType.PLAN_2}
          showPopup={!canChangeBackground}
        >
          <Radio
            value={AtiraLinkStylesBackgroundMode.IMAGE}
            disabled={!canChangeBackground}
            className={atiraLinkTestIDs.design.background.options.asImage}
          >
            {t('atiraLink.create.design.background.asImage')}
          </Radio>
        </PaidFeatureNotice>
      </Radio.Group>

      {mode === AtiraLinkStylesBackgroundMode.COLOR ? (
        <Controller
          control={control}
          name="styles.background.color"
          rules={{ required: false }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <InputWrapper>
              <AtiraColorPicker
                title={t('atiraLink.create.design.background.color')}
                onChange={(v) => onChange(v?.toHexString())}
                value={value}
                errorMessage={error?.message}
                valid={!error}
                allowClear
                onClear={() => onChange(null)}
                dataTestId={atiraLinkTestIDs.design.background.color}
              />
            </InputWrapper>
          )}
        />
      ) : (
        <Flex flexDirection="column" gap="m">
          <Controller
            control={control}
            name="styles.backgroundImage"
            rules={{ required: false }}
            render={({ field: { onChange } }) => (
              <ImageUploadWrapper>
                <ImageUpload
                  title={t('atiraLink.create.design.background.image')}
                  maxCount={1}
                  onRemove={() => {
                    setValue('styles.backgroundImage', '');
                    setValue(
                      'styles.background.backgroundMode',
                      AtiraLinkStylesBackgroundMode.COLOR,
                    );
                  }}
                  onChange={onChange}
                  image={
                    (backgroundImage as any) instanceof File
                      ? URL.createObjectURL(backgroundImage as any)
                      : backgroundImage || ''
                  }
                  disabled={!canChangeBackground}
                  dataTestId={atiraLinkTestIDs.design.background.image}
                  showAspectRatioTooltip={false}
                />
              </ImageUploadWrapper>
            )}
          />

          {mode === AtiraLinkStylesBackgroundMode.IMAGE ? (
            <ResponsiveFlex>
              <Controller
                control={control}
                name="styles.background.backgroundImageSize"
                rules={{ required: false }}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <InputWrapper>
                    <AtiraSelect
                      title={t('atiraLink.create.design.background.image.size')}
                      onChange={onChange}
                      value={value || AtiraLinkBackgroundImageSize.AUTO}
                      errorMessage={error?.message}
                      valid={!error}
                      options={backgroundImageSizeOptions(
                        t,
                        atiraLinkTestIDs.design.background.imageSizeOption,
                      )}
                      dataTestId={atiraLinkTestIDs.design.background.imageSize}
                      info={
                        <Trans
                          i18nKey={
                            'atiraLink.create.design.background.image.size.info'
                          }
                          components={[<br />]}
                        />
                      }
                    />
                  </InputWrapper>
                )}
              />

              <Controller
                control={control}
                name="styles.background.backgroundImagePosition"
                rules={{ required: false }}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <InputWrapper>
                    <AtiraSelect
                      title={t(
                        'atiraLink.create.design.background.image.position',
                      )}
                      onChange={onChange}
                      value={value || AtiraLinkBackgroundImagePosition.AUTO}
                      errorMessage={error?.message}
                      valid={!error}
                      options={backgroundImagePositionOptions(
                        t,
                        atiraLinkTestIDs.design.background.imagePositionOption,
                      )}
                      dataTestId={
                        atiraLinkTestIDs.design.background.imagePosition
                      }
                      info={t(
                        'atiraLink.create.design.background.image.position.info',
                      )}
                    />
                  </InputWrapper>
                )}
              />
            </ResponsiveFlex>
          ) : null}
        </Flex>
      )}
    </StyledSection>
  );
};
